import {
  Button,
  Col,
  Flex,
  Form,
  Image,
  Input,
  Pagination,
  Row,
  Select,
  Space,
  Table,
  TableColumnType,
  Tabs,
  Tag,
} from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './index.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getReportCategory, getReportList } from '@/api/report';
import { Report, ReportListQuery, ReportStatus } from '@/types/report';
import dayjs from 'dayjs';
import { FORMAT_TIME } from '@/types/common';
import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';

export const REPORT_IDS_KEY = 'report_ids';
export const REPORT_INDEX_KEY = 'report_index';

const ReportPage: React.FC = () => {
  const navigate = useNavigate();
  const [form] =
    Form.useForm<
      Omit<ReportListQuery, 'currentPage' | 'pageSize' | 'status'>
    >();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useAtomValue(isMobileAtom);

  const pageSize = isMobile ? 10 : 20;
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [tab, setTab] = useState('all');

  useEffect(() => {
    form.setFieldsValue(Object.fromEntries(searchParams));
    const page = searchParams.get('page') || '1';
    setPage(parseInt(page));
    const tab = searchParams.get('status') || 'all';
    setTab(tab);
  }, [searchParams, form]);

  const { data: reportCategory } = useQuery({
    queryKey: ['reportCategory'],
    queryFn: getReportCategory,
    refetchOnWindowFocus: false,
  });

  const reportIssueObjectRef = useRef<
    Record<string, { label: string; children: { [key: string]: string } }>
  >({});

  const reportReason = useMemo<{ value: string; label: string }[]>(() => {
    if (!reportCategory) return [];
    const _reportCategory: { value: string; label: string }[] = [];

    reportCategory?.data.forEach((item) => {
      _reportCategory.push({
        value: item.categoryId.toString(),
        label: item.categoryName,
      });

      item.children.forEach((child) => {
        if (!reportIssueObjectRef.current[item.categoryId.toString()]) {
          reportIssueObjectRef.current[item.categoryId.toString()] = {
            label: item.categoryName,
            children: {
              [child.categoryId.toString()]: child.categoryName,
            },
          };
        } else {
          reportIssueObjectRef.current[item.categoryId.toString()].children[
            child.categoryId.toString()
          ] = child.categoryName;
        }
      });
    });
    return _reportCategory;
  }, [reportCategory]);

  const [reportIssue, setReportIssue] = useState<
    {
      value: string;
      label: string;
    }[]
  >([]);

  const handleFormValueChange = (value: Record<string, string>) => {
    // 初始化报告原因
    if (value.categoryId) {
      form.setFieldValue('subCategoryId', '');
      const children = reportIssueObjectRef.current[value.categoryId].children;
      if (children) {
        const _reportIssue = Object.keys(children).map((item) => ({
          value: item,
          label: children[item],
        }));
        setReportIssue(_reportIssue);
      }
    }
  };

  const columns: TableColumnType<Report>[] = [
    {
      title: 'Report ID',
      dataIndex: 'reportId',
      key: 'reportId',
    },
    {
      title: 'Report User ID',
      dataIndex: 'reportUserId',
      key: 'reportUserId',
    },
    {
      title: 'Name',
      dataIndex: 'reportUserName',
      key: 'reportUserName',
      render: (_, record) =>
        record.reportUserName || record.reportUserNickName || '-',
    },
    {
      title: 'The Report User ID',
      dataIndex: 'theReportUserId',
      key: 'theReportUserId',
    },
    {
      title: 'Name',
      dataIndex: 'theReportUserName',
      key: 'theReportUserName',
      render: (_, record) =>
        record.theReportUserName || record.theReportUserNickName || '-',
    },
    {
      title: 'Pictures',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      render: (_, record) => (
        <Image src={record.imageUrl} width={100} />
      ),
    },
    {
      title: 'Report Reason',
      dataIndex: 'categoryId',
      key: 'categoryId',
      render: (_, record) =>
        reportIssueObjectRef.current[record.categoryId.toString()]?.label ||
        '-',
    },
    {
      title: 'Report Issue',
      dataIndex: 'subCategoryId',
      key: 'subCategoryId',
      render: (_, record) =>
        reportIssueObjectRef.current[record.categoryId.toString()]?.children[
          record.subCategoryId.toString()
        ] || '-',
    },
    {
      title: 'Description',
      dataIndex: 'userRemark',
      key: 'userRemark',
    },
    {
      title: 'Content ID',
      dataIndex: 'artworkId',
      key: 'artworkId',
    },
    {
      title: 'type',
      dataIndex: 'type',
      key: 'type',
      render: (v: number | undefined) =>
        v === 1 ? '话题' : v === 0 ? '作品' : '-',
    },
    {
      title: 'Content Status',
      dataIndex: 'status',
      key: 'status',
      render: (_, record) => {
        switch (record.status) {
          case ReportStatus.NOT_YET:
            return <Tag>Pending</Tag>;
          case ReportStatus.BANNED:
            return <Tag color="red">Banned</Tag>;
          case ReportStatus.OMMITTED:
            return <Tag color="green">Omitted</Tag>;
          default:
            return '-';
        }
      },
    },
    {
      title: 'Report Time',
      dataIndex: 'createTime',
      key: 'createTime',
      render: (_, record) =>
        dayjs(record.createTime).format(FORMAT_TIME) || '-',
    },
    {
      title: '操作人',
      dataIndex: 'updateUserName',
      key: 'updateUserName',
      render: (_, record) => record.updateUserName || '-',
    },
    {
      title: '操作时间',
      dataIndex: 'updateTime',
      key: 'updateTime',
      render: (_, record) =>
        record.updateTime ? dayjs(record.updateTime).format(FORMAT_TIME) : '-',
    },
    {
      title: 'Operation',
      key: 'operation',
      width: 120,
      fixed: 'right',
      render: (_, record: Report, index: number) => (
        <Space size="small" direction="vertical">
          <Button type="link" onClick={() => handleViewClick(record, index)}>
            View
          </Button>
          <Button type="link" onClick={() => handleJudgeClick(record, index)}>
            Judge
          </Button>
        </Space>
      ),
    },
  ];

  const {
    data: reportList,
    isLoading,
    refetch: refetchReportList,
  } = useQuery({
    queryKey: ['reportList', tab, page],
    queryFn: async () => {
      const values = form.getFieldsValue();
      const query: ReportListQuery = {
        ...values,
        pageSize,
        currentPage: page,
        status: tab === 'all' ? '' : tab,
      };
      if (values.type !== undefined && values.type !== '') {
        query.type = Number(values.type);
      }
      const resp = await getReportList(query);
      if (resp.status !== 200) {
        return [];
      }
      setTotal(resp.data.total);
      return resp.data.records;
    },
    refetchOnWindowFocus: false,
  });

  const handleViewClick = (record: Report, index: number) => {
    const ids = reportList?.map((item) => item.reportId) || [];
    const params = new URLSearchParams();
    params.set(REPORT_IDS_KEY, ids.join(','));
    params.set(REPORT_INDEX_KEY, index.toString());
    if (record.type !== undefined && record.type !== null) {
      params.set('type', String(record.type));
    }
    navigate(`/report/view?${params.toString()}`);
  };

  const handleJudgeClick = (record: Report, index: number) => {
    const ids = reportList?.map((item) => item.reportId) || [];
    const params = new URLSearchParams();
    params.set(REPORT_IDS_KEY, ids.join(','));
    params.set(REPORT_INDEX_KEY, index.toString());
    if (record.type !== undefined && record.type !== null) {
      params.set('type', String(record.type));
    }
    navigate(`/report/judge?${params.toString()}`);
  };

  const handleSearch = () => {
    const params = new URLSearchParams(searchParams);
    Object.entries(form.getFieldsValue()).forEach(([key, value]) => {
      if (value) {
        params.set(key, value.toString());
      } else {
        params.delete(key);
      }
    });
    params.set('page', '1');
    setSearchParams(params);
    setPage(1);
    void refetchReportList();
  };

  const handleTabChange = (key: string) => {
    setPage(1);
    setTab(key);
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    if (key !== 'all') {
      params.set('status', key);
    } else {
      params.delete('status');
    }
    setSearchParams(params);
  };

  return (
    <div className="report-container">
      <Form
        form={form}
        labelCol={{
          span: 8,
        }}
        wrapperCol={{
          span: 16,
        }}
        labelAlign="left"
        onValuesChange={handleFormValueChange}
        onFinish={handleSearch}
      >
        <Row gutter={24}>
          <Col span={12} xs={24} sm={12} md={8}>
            <Form.Item name="artworkId" label="Artwork ID">
              <Input size="small" />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} sm={12} md={8}>
            <Form.Item name="reportId" label="Report ID">
              <Input size="small" />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} sm={12} md={8}>
            <Form.Item name="categoryId" label="Report Reason">
              <Select options={reportReason} allowClear size="small" />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} sm={12} md={8}>
            <Form.Item name="subCategoryId" label="Report Issue">
              <Select options={reportIssue} allowClear size="small" />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} sm={12} md={8}>
            <Form.Item name="reportUserId" label="Report User Id">
              <Input size="small" />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} sm={12} md={8}>
            <Form.Item name="theReportUserId" label="The Report User Id">
              <Input size="small" />
            </Form.Item>
          </Col>
          <Col span={12} xs={24} sm={12} md={8}>
            <Form.Item name="type" label="type">
              <Select
                allowClear
                placeholder="请选择"
                size="small"
                options={[
                  { label: '话题', value: 1 },
                  { label: '作品', value: 0 },
                ]}
              />
            </Form.Item>
          </Col>
          {/* <Col span={12} xs={24} sm={12} md={8}>
            <Form.Item name="createTime" label="Report Date">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col> */}
        </Row>
        <div>
          <Button type="primary" htmlType="submit">
            Search
          </Button>
        </div>
      </Form>
      <div style={{ paddingTop: '10px' }}>
        <Tabs
          type="card"
          items={[
            { key: 'all', label: 'All' },
            { key: ReportStatus.NOT_YET.toString(), label: 'Pending' },
            { key: ReportStatus.BANNED.toString(), label: 'Banned' },
            { key: ReportStatus.OMMITTED.toString(), label: 'Omitted' },
          ]}
          activeKey={tab}
          className="tabs-container"
          onChange={handleTabChange}
          size="small"
        />
      </div>
      <Table
        rowKey="reportId"
        columns={columns}
        dataSource={reportList}
        scroll={{ x: 'max-content' }}
        pagination={false}
        loading={isLoading}
        size="small"
      />
      <Flex style={{ marginTop: 20, flexDirection: 'row-reverse' }}>
        <Pagination
          size={isMobile ? 'small' : 'default'}
          total={total}
          pageSize={pageSize}
          current={page}
          showSizeChanger={false}
          onChange={(page) => {
            setPage(page);
            const params = new URLSearchParams(searchParams);
            params.set('page', page.toString());
            setSearchParams(params);
          }}
        />
      </Flex>
    </div>
  );
};

export default ReportPage;
